import { limitValue } from '../../../../utils';

interface LabelSize {
  width: number;
  height: number;
}

interface LabelSizes {
  first: LabelSize;
  last: LabelSize;
  widest: LabelSize;
  highest: LabelSize;
  widths: number[];
  heights: number[];
}

interface AxisSize {
  width: number;
  height: number;
}

const calculateRotatedLabelOffset = (
  labelSizes: LabelSizes,
  rotation: number,
  maxRotation = 90,
  scaleType: 'point' | 'band' = 'band',
  availableWidth?: number
): { x: number; y: number } => {
  const rot = (rotation * Math.PI) / 180;
  const cos = Math.abs(Math.cos(rot));
  const sin = Math.abs(Math.sin(rot));

  // Assume labels are below ticks (like Chart.js default)
  const isRotated = rotation !== 0;

  let paddingLeft = 0;
  let paddingTop = 0;

  if (isRotated) {
    // For rotated labels below ticks
    // Use a smoother transition for small angles
    const angleFactor = Math.min(rotation / 100, 1); // Smooth transition for first 10 degrees

    // Calculate base x-offset
    const baseXOffset = cos * labelSizes.first.width * angleFactor;

    // Add minimum offset for high rotation angles
    const rotationFactor = Math.min(rotation / maxRotation, 1);

    // Different offset calculations for different scale types
    if (scaleType === 'band') {
      // For band scale, use larger offset
      const aggressiveRotationFactor = Math.pow(rotationFactor, 0.5);
      const minOffset = labelSizes.first.width * 0.45;
      // Add extra offset for high rotation angles with reduced impact
      const highRotationFactor =
        rotation > 45 ? ((rotation - 45) / 45) * 0.3 : 0;
      const additionalXOffset =
        minOffset * (aggressiveRotationFactor + highRotationFactor);
      paddingLeft = baseXOffset + additionalXOffset;
    } else {
      // For point scale, use smaller offset
      const aggressiveRotationFactor = Math.pow(rotationFactor, 0.5);
      const minOffset = labelSizes.first.width * 0.25;
      const additionalXOffset = minOffset * aggressiveRotationFactor;
      paddingLeft = baseXOffset + additionalXOffset;
    }

    // Calculate base y-offset
    const baseYOffset = sin * labelSizes.last.height;

    // Add extra y-offset that increases as available width decreases
    const maxExtraOffset = labelSizes.highest.height * 1; // Maximum extra offset
    const minWidth = 400; // Width at which we start adding extra offset
    const extraYOffset =
      availableWidth && availableWidth < minWidth
        ? maxExtraOffset * (1 - availableWidth / minWidth)
        : 0;

    if (scaleType === 'point') {
      // For point scale, we need more vertical space to prevent overlap
      const additionalPadding =
        labelSizes.highest.height * (0.5 + rotationFactor * 0.5);
      paddingTop = baseYOffset + additionalPadding + extraYOffset;
    } else {
      paddingTop = baseYOffset + extraYOffset;
    }
  } else {
    // For non-rotated labels, use center alignment
    paddingLeft = labelSizes.first.width / 2;
    paddingTop = labelSizes.highest.height / 2;
  }

  // Add the base padding
  paddingLeft = Math.max(paddingLeft, 0);
  paddingTop = Math.max(paddingTop, 0);

  return {
    x: Math.round(paddingLeft),
    y: Math.round(paddingTop),
  };
};

const measureLabelSizes = (labels: string[], fontSize: number): LabelSizes => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return {
      first: { width: 0, height: 0 },
      last: { width: 0, height: 0 },
      widest: { width: 0, height: 0 },
      highest: { width: 0, height: 0 },
      widths: [],
      heights: [],
    };
  }
  const widths: number[] = [];
  const heights: number[] = [];

  ctx.font = `${fontSize}px sans-serif`;

  labels.forEach((label) => {
    const metrics = ctx.measureText(label);
    const width = metrics.width;
    const height = fontSize; // Approximate height based on fontSize

    widths.push(width);
    heights.push(height);
  });

  const widestIdx = widths.indexOf(Math.max(...widths));
  const highestIdx = heights.indexOf(Math.max(...heights));

  return {
    first: { width: widths[0], height: heights[0] },
    last: {
      width: widths[widths.length - 1],
      height: heights[heights.length - 1],
    },
    widest: { width: widths[widestIdx], height: heights[widestIdx] },
    highest: { width: widths[highestIdx], height: heights[highestIdx] },
    widths,
    heights,
  };
};

export const calculateLabelRotation = (
  labels: string[],
  availableWidth: number,
  options: { padding?: number; scaleType?: 'point' | 'band' } = {}
) => {
  // Create canvas context for text measurement
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  const minRotation = 0;
  const maxRotation = 45;
  if (!ctx) {
    return {
      rotation: minRotation,
      offset: { x: 0, y: 0 },
    };
  }
  const { padding = 10, scaleType = 'band' } = options;
  const labelSizes = measureLabelSizes(labels, 12);
  // Calculate number of ticks and tick width
  const numTicks = labels.length;
  const maxLabelWidth = labelSizes.widest.width;

  const maxWidth = Math.max(availableWidth - maxLabelWidth, 0);
  const tickWidth =
    scaleType === 'band' ? maxWidth / numTicks : maxWidth / (numTicks - 1);
  // If labels fit horizontally, don't rotate
  if (maxLabelWidth + padding <= tickWidth) {
    return {
      rotation: minRotation,
      offset: { x: 0, y: 0 },
      size: fitLabels(labelSizes, 0, padding),
    };
  }

  // Calculate rotation based on available space
  let labelRotation = toDegrees(
    Math.asin(
      limitValue((labelSizes.highest.height + padding * 2) / tickWidth, -1, 1)
    )
  );

  // Clamp rotation between 0 and maxRotation degrees
  labelRotation = limitValue(labelRotation, minRotation, maxRotation);

  // Calculate the required size and offset based on rotation
  const size = fitLabels(labelSizes, labelRotation, padding);
  const offset = calculateRotatedLabelOffset(
    labelSizes,
    labelRotation,
    maxRotation,
    scaleType,
    availableWidth
  );

  return {
    rotation: labelRotation,
    offset,
    size,
  };
};

const fitLabels = (
  labelSizes: LabelSizes,
  rotation: number,
  padding: number
): AxisSize => {
  const angleRadians = (rotation * Math.PI) / 180;
  const cos = Math.abs(Math.cos(angleRadians));
  const sin = Math.abs(Math.sin(angleRadians));

  // Calculate space needed for labels using Chart.js approach
  const tickPadding = padding * 2;
  const labelHeight =
    sin * labelSizes.widest.width + cos * labelSizes.highest.height;
  const labelWidth =
    cos * labelSizes.widest.width + sin * labelSizes.highest.height;

  return {
    width: labelWidth + tickPadding,
    height: labelHeight + tickPadding,
  };
};

// Helper functions
const toDegrees = (rad: number): number => {
  return rad * (180 / Math.PI);
};

// utils for ChartTimePeriod
const calculateMaxVisibleTickLabelLengthChartTimePeriod = (
  availableWidth: number,
  numTicks: number
) => {
  const sizePerTick = availableWidth / numTicks;
  if (sizePerTick > 100) {
    return 15;
  } else {
    return 12;
  }
};

const calculateTickFontSizeChartTimePeriod = (
  availableWidth: number,
  numTicks: number
) => {
  // if too many ticks for limited space, reduce font size
  const sizePerTick = availableWidth / numTicks;
  if (sizePerTick > 20) {
    return 12;
  }
  if (sizePerTick > 10) {
    return 11;
  }
  return 10;
};

const calculateMarginsChartTimePeriod = (
  areTicksRotated: boolean,
  maxLabelSize: number,
  hasRightAxis: boolean
) => {
  const rightMargin = hasRightAxis ? 30 : 0;
  const topMargin = 20;
  let leftMargin;
  let bottomMargin;
  if (!areTicksRotated) {
    leftMargin = 40;
    bottomMargin = 30;
  } else {
    if (maxLabelSize > 80) {
      leftMargin = 60;
      bottomMargin = 70;
    } else if (maxLabelSize > 60) {
      leftMargin = 60;
      bottomMargin = 60;
    } else if (maxLabelSize > 40) {
      leftMargin = 50;
      bottomMargin = 50;
    } else {
      leftMargin = 40;
      bottomMargin = 40;
    }
  }
  return {
    right: rightMargin,
    left: leftMargin,
    top: topMargin,
    bottom: bottomMargin,
  };
};

const calculateLabelRotationChartTimePeriod = (
  tickLabels: string[],
  availableWidth: number,
  fontSize: number
): {
  rotation: number;
  labelSizes: LabelSizes;
} => {
  const labelSizes = measureLabelSizes(tickLabels, fontSize);
  // Calculate number of ticks and tick width
  const numTicks = tickLabels.length;
  const maxLabelWidth = labelSizes.widest.width;

  const tickWidth = availableWidth / numTicks;
  // If labels fit horizontally, don't rotate
  if (maxLabelWidth <= tickWidth) {
    return {
      rotation: 0,
      labelSizes,
    };
  }

  return {
    rotation: -30,
    labelSizes,
  };
};

export const getChartTimePeriodAxisConfig = (
  labels: string[],
  availableWidth: number
): {
  margins: {
    left: number;
    right: number;
    top: number;
    bottom: number;
  };
  tickRotation: number;
  tickFontSize: number;
  maxVisibleTickLabelLength: number;
} => {
  const tickFontSize = calculateTickFontSizeChartTimePeriod(
    availableWidth,
    labels.length
  );
  const maxVisibleTickLabelLength =
    calculateMaxVisibleTickLabelLengthChartTimePeriod(
      availableWidth,
      labels.length
    );
  const shortenedTickLabels = labels.map((label) =>
    label.slice(0, maxVisibleTickLabelLength)
  );
  const { rotation, labelSizes } = calculateLabelRotationChartTimePeriod(
    shortenedTickLabels,
    availableWidth,
    tickFontSize
  );
  const margins = calculateMarginsChartTimePeriod(
    rotation !== 0,
    labelSizes.widest.width,
    false
  );
  return {
    tickRotation: rotation,
    tickFontSize,
    margins,
    maxVisibleTickLabelLength,
  };
};
